/********************************************
 * Constants and Types
 ********************************************/
const notify = {};
const NOTIFICATIONS_WRAPPER_ID = 'rp-notifications-wrapper';
const NOTIFICATION_TEMPLATE_ID = 'rp-notification-template';
const NOTIFICATION_DELAY_MS = 5000;
const NOTIFICATION_ANIMATION_DURATION_MS = 250;
const NotificationType = Object.freeze({
	ERROR: 'error',
	INFO: 'info',
	SUCCESS: 'success',
	WARNING: 'warning',
});

/**
 * @typedef {object} NotificationAction
 * @property {string} label Button label text.
 * @property {(() => void)=} onClick Optional click callback.
 */

/**
 * @typedef {object} NotificationOptionsInput
 * @property {NotificationAction=} action Optional action config.
 * @property {number=} delay Optional auto-dismiss delay in milliseconds.
 * @property {string=} id Optional DOM id to make a notification unique.
 * @property {string} message Notification body text.
 * @property {string=} title Optional notification title text.
 * @property {string=} type NotificationType-like value.
 */

/**
 * @typedef {object} NotificationOptions
 * @property {NotificationAction|null} action Optional action config.
 * @property {number|null} delay Auto-dismiss delay in milliseconds.
 * @property {string} id Optional DOM id.
 * @property {string} message Notification body text.
 * @property {string} title Optional notification title text.
 * @property {string} type Normalized notification type.
 */

/********************************************
 * Exported API
 ********************************************/

/**
 * Renders a notification toast, deduplicates equivalent ones, and optionally auto-dismisses.
 *
 * @param {NotificationOptionsInput} options Notification options.
 * @returns {void}
 */
notify.add_notification = function (options) {
	const normalizedOptions = normalizeNotificationOptions(options);
	if (normalizedOptions === null) {
		return;
	}

	if (normalizedOptions.id !== '' && document.getElementById(normalizedOptions.id)) {
		return;
	}

	const notificationsWrapper = document.getElementById(NOTIFICATIONS_WRAPPER_ID);
	if (!notificationsWrapper) {
		return;
	}

	const notificationSignature = getNotificationSignature(normalizedOptions);
	if (getDuplicateNotification(notificationsWrapper, notificationSignature)) {
		return;
	}

	const notificationElement = buildNotificationElement(normalizedOptions);
	if (!notificationElement) {
		return;
	}
	notificationElement.dataset.notificationSignature = notificationSignature;
	notificationElement.classList.add('rp-notification--enter');

	const previousPositions = captureNotificationPositions(getNotifications(notificationsWrapper));
	notificationsWrapper.appendChild(notificationElement);
	animateNotificationEnter(notificationElement);
	syncNotificationWidths(notificationsWrapper);
	animateNotificationReflow(getNotifications(notificationsWrapper), previousPositions);
	scheduleNotificationDismiss(notificationElement, normalizedOptions.delay);
};

/**
 * Removes all notifications currently rendered in the notifications wrapper.
 *
 * @returns {void}
 */
notify.clear_notifications = function () {
	const notificationsWrapper = document.getElementById(NOTIFICATIONS_WRAPPER_ID);
	if (notificationsWrapper) {
		notificationsWrapper.innerHTML = '';
	}
};

/********************************************
 * Private helpers
 ********************************************/

const VALID_NOTIFICATION_TYPES = new Set(Object.values(NotificationType));

/**
 * Normalizes arbitrary type input to a supported notification type.
 *
 * @param {string|undefined} type Raw notification type input.
 * @returns {string} A value from NotificationType.
 */
function normalizeType(type) {
	const normalized = String(type || NotificationType.INFO).toLowerCase();
	return VALID_NOTIFICATION_TYPES.has(normalized) ? normalized : NotificationType.INFO;
}

/**
 * Validates and normalizes notification options for runtime usage.
 *
 * @param {unknown} options Raw options object from callers.
 * @returns {NotificationOptions|null} Normalized options or null when invalid.
 */
function normalizeNotificationOptions(options) {
	if (typeof options !== 'object' || options === null) {
		return null;
	}

	const message = String(options.message || '').trim();
	if (message === '') {
		return null;
	}

	const delayRaw = Number(options.delay);
	let delay = NOTIFICATION_DELAY_MS;
	if (Number.isFinite(delayRaw)) {
		delay = delayRaw > 0 ? delayRaw : null;
	}
	const normalizedType = normalizeType(options.type);

	return {
		action: options.action || null,
		delay,
		id: typeof options.id === 'string' ? options.id : '',
		message,
		title: String(options.title || normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1)),
		type: normalizedType,
	};
}

/**
 * Builds a stable signature used to deduplicate equivalent notifications.
 *
 * @param {NotificationOptions} options Normalized notification options.
 * @returns {string} Signature string for duplicate detection.
 */
function getNotificationSignature(options) {
	return [
		options.type,
		options.title,
		options.message,
		options.action?.label ?? '',
	].join('|');
}

/**
 * Finds an existing notification that matches the given signature.
 *
 * @param {HTMLElement} notificationsWrapper Wrapper containing notifications.
 * @param {string} signature Signature to match.
 * @returns {HTMLElement|null} Existing notification element or null.
 */
function getDuplicateNotification(notificationsWrapper, signature) {
	const notifications = notificationsWrapper.querySelectorAll('.rp-notification:not(.rp-notification--hiding)');
	for (const notification of notifications) {
		if (/** @type {HTMLElement} */(notification).dataset.notificationSignature === signature) {
			return /** @type {HTMLElement} */(notification);
		}
	}
	return null;
}

/**
 * Clones template markup and hydrates it with normalized notification options.
 *
 * @param {NotificationOptions} options Normalized notification options.
 * @returns {HTMLElement|null} Rendered notification element or null on failure.
 */
function buildNotificationElement(options) {
	const template = document.getElementById(NOTIFICATION_TEMPLATE_ID);
	if (!template?.content) {
		return null;
	}

	const clonedNode = template.content.cloneNode(true).firstElementChild;
	if (!(clonedNode instanceof HTMLElement)) {
		return null;
	}

	const titleEl = clonedNode.querySelector('.rp-notification__title');
	const messageEl = clonedNode.querySelector('.rp-notification__message');
	const actionContainer = clonedNode.querySelector('.rp-notification__action-container');
	const actionButton = clonedNode.querySelector('.rp-notification__action');
	if (!titleEl || !messageEl || !actionContainer || !(actionButton instanceof HTMLButtonElement)) {
		return null;
	}

	if (options.id !== '') {
		clonedNode.setAttribute('id', options.id);
	}

	clonedNode.classList.add(`rp-notification--${options.type}`);

	if (options.type === 'error' || options.type === 'warning') {
		clonedNode.setAttribute('role', 'alert');
		clonedNode.setAttribute('aria-live', 'assertive');
	} else {
		clonedNode.setAttribute('role', 'status');
		clonedNode.setAttribute('aria-live', 'polite');
	}

	titleEl.textContent = options.title;
	messageEl.textContent = options.message;

	const action = options.action;
	if (!action || typeof action.label !== 'string' || action.label.trim() === '') {
		actionContainer.remove();
	} else {
		actionButton.textContent = action.label;
		actionButton.addEventListener('click', () => action.onClick?.());
	}

	return clonedNode;
}

/**
 * Returns visible notification elements in render order.
 *
 * @param {HTMLElement} notificationsWrapper Wrapper containing notifications.
 * @returns {HTMLElement[]} Notification elements.
 */
function getNotifications(notificationsWrapper) {
	return /** @type {HTMLElement[]} */(
		Array.from(notificationsWrapper.querySelectorAll('.rp-notification:not(.rp-notification--hiding)'))
	);
}

/**
 * Normalizes stacked notification widths so all visible toasts align to the widest.
 *
 * @param {HTMLElement} notificationsWrapper Wrapper containing notifications.
 * @returns {void}
 */
function syncNotificationWidths(notificationsWrapper) {
	const notifications = getNotifications(notificationsWrapper);

	for (const notification of notifications) {
		notification.style.removeProperty('width');
	}

	if (notifications.length <= 1) {
		return;
	}

	const maxWidth = Math.max(...notifications.map((n) => n.getBoundingClientRect().width));
	for (const notification of notifications) {
		notification.style.width = `${Math.ceil(maxWidth)}px`;
	}
}

/**
 * Captures current notification bounds before a layout change for FLIP animation.
 *
 * @param {HTMLElement[]} notifications Notification elements.
 * @returns {Map<HTMLElement, DOMRect>} Map of elements to pre-update positions.
 */
function captureNotificationPositions(notifications) {
	const positions = new Map();
	for (const notification of notifications) {
		positions.set(notification, notification.getBoundingClientRect());
	}
	return positions;
}

/**
 * Performs FLIP-style motion so existing notifications move smoothly on reflow.
 *
 * @param {HTMLElement[]} notifications Notification elements.
 * @param {Map<HTMLElement, DOMRect>} previousPositions Previous element bounds.
 * @returns {void}
 */
function animateNotificationReflow(notifications, previousPositions) {
	for (const notification of notifications) {
		if (
			notification.classList.contains('rp-notification--enter') ||
			notification.classList.contains('rp-notification--hiding')
		) {
			continue;
		}

		const previousRect = previousPositions.get(notification);
		if (!previousRect) {
			continue;
		}

		const deltaY = previousRect.top - notification.getBoundingClientRect().top;
		if (Math.abs(deltaY) < 0.5) {
			continue;
		}

		notification.style.transition = 'none';
		notification.style.transform = `translateY(${deltaY}px)`;
		// Force a synchronous reflow so the browser commits the initial transform
		// before re-enabling transitions. Without this, the browser batches both
		// style changes and no animation occurs.
		notification.offsetHeight; // eslint-disable-line no-unused-expressions
		notification.style.transition = '';
		notification.style.transform = '';
	}
}

/**
 * Triggers enter animation by removing the pre-enter class on next frame.
 *
 * @param {HTMLElement} notificationElement Newly inserted notification element.
 * @returns {void}
 */
function animateNotificationEnter(notificationElement) {
	requestAnimationFrame(() => {
		notificationElement.classList.remove('rp-notification--enter');
	});
}

/**
 * Schedules auto-dismiss for a notification and animates stack reflow after removal.
 *
 * @param {HTMLElement} notificationElement Notification element to dismiss.
 * @param {number|null} delay Auto-dismiss delay in milliseconds.
 * @returns {void}
 */
function scheduleNotificationDismiss(notificationElement, delay) {
	if (delay === null) {
		return;
	}

	setTimeout(() => {
		if (!notificationElement.isConnected) {
			return;
		}

		notificationElement.classList.add('rp-notification--hiding');
		setTimeout(() => {
			const notificationsWrapper = notificationElement.parentElement;
			if (!notificationsWrapper) {
				return;
			}
			const previousPositions = captureNotificationPositions(getNotifications(notificationsWrapper));
			notificationElement.remove();
			syncNotificationWidths(notificationsWrapper);
			animateNotificationReflow(getNotifications(notificationsWrapper), previousPositions);
		}, NOTIFICATION_ANIMATION_DURATION_MS);
	}, delay);
}

module.exports = { notify, NotificationType };
